<template>
  <div
    :data-entry-type="item.type"
    :data-test-id="generateDataTestId('calendar', item.name)"
    :data-datetime="datetimeTimestamp"
    :data-date="dateString"
    :class="[
      'text-xs mb-1 relative overflow-hidden cursor-pointer border-1 border flex transition-colors duration-100',
      colorClasses,
      isDraft ? 'bg-surface-light rounded-md border-transparent' : 'border-transparent',
      // Single-day items (both isStart and isEnd are true)
      isStart && isEnd ? 'rounded-md mx-3' : '',
      // Multi-day spanning - start day
      isStart && !isEnd ? 'rounded-l-md rounded-r-none relative -mr-[1px] pr-[1px] ml-3' : '',
      // Multi-day spanning - continuation
      isContinuation ? 'rounded-none border-x-0 relative -mx-[1px] px-[1px]' : '',
      // Multi-day spanning - end day
      isEnd && !isStart ? 'rounded-r-md rounded-l-none border-l-0 relative -ml-[1px] pl-[1px] mr-3' : '',
    ]"
    :style="colorStyle"
    @click="$emit('click', item)"
  >
    <!--
      Draft border: an SVG stroked rect gives a dashed outline that follows the
      rounded corners (border-dashed / gradients can't round). No viewBox means
      percentages map to pixels 1:1, so dashes and radius stay crisp at any size.
    -->
    <svg
      v-if="isDraft"
      class="am3-calendar-draft-border pointer-events-none absolute inset-0 z-10 h-full w-full"
      aria-hidden="true"
    >
      <rect class="am3-calendar-draft-rect" />
    </svg>

    <!-- Continuous background image spanning all 7 grid columns; opacity + blur let the event color show through -->
    <div
      v-if="image"
      class="absolute -inset-1 z-0 opacity-90 blur-sm"
      :style="imageBackgroundStyle"
    />

    <div
      class="flex items-center gap-1 [&>div]:min-w-4 relative z-20 flex-auto truncate py-1 px-2 font-bold  max-w-full text-ellipsis"
      :class="isWeekStart ? 'pl-5' : ''"
    >
      <img
        v-if="image && showContent && item.type === 'event'"
        :src="image"
        :alt="item.name"
        class="size-4 rounded-sm object-cover flex-shrink-0"
      >
      <AM3Icon
        v-else-if="iconType && showContent"
        :type="iconType"
        size="sm"
        color="currentColor"
      />
      <p
        v-if="showContent"
        class="truncate flex-shrink text-xs select-none"
      >
        {{ item.name }}
      </p>
      <span
        v-else
        class="opacity-0"
      >&nbsp;</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import type { AnyCalendarItem } from '@/pages/events/types/calendarItems'
import { CALENDAR_ITEM_COLORS, hasDraft } from '@/pages/events/types/calendarItems'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { toDateString, toTimestamp } from '@/utils/formatters'
import { useColorContrast } from '@/composables/useColorContrast'

interface Props {
  item: AnyCalendarItem
  isStart?: boolean
  isEnd?: boolean
  isContinuation?: boolean
  isWeekStart?: boolean
  image?: string
  dayOffset?: number
  totalSpanDays?: number
}

const props = withDefaults(defineProps<Props>(), {
  isStart: false,
  isEnd: false,
  isContinuation: false,
  isWeekStart: false,
  image: undefined,
  dayOffset: 0,
  totalSpanDays: 1,
})

const emit = defineEmits<{
  click: [item: AnyCalendarItem]
}>()

const { getContrastColor } = useColorContrast()

// Convert datetime to timestamp
const datetimeTimestamp = computed(() => {
  return toTimestamp(props.item.datetime)
})

// Extract date in YYYY-MM-DD format
const dateString = computed(() => {
  return toDateString(props.item.datetime)
})

// Compute if the item is a draft
const isDraft = computed(() => {
  return hasDraft(props.item) && props.item.draft
})

// Show label/icon when this is the first visible cell for this item in the current row
const showContent = computed(() => {
  return props.isStart || props.isWeekStart || (!props.isStart && !props.isContinuation && !props.isEnd)
})

// Get color classes based on item type
const colorClasses = computed(() => {
  const colors = CALENDAR_ITEM_COLORS[props.item.type]

  // Custom items with a user-defined color use colorStyle (inline) instead
  if (props.item.type === 'customItem' && props.item.color) {
    return ''
  }

  if (isDraft.value) {
    // Border color is rendered via the am3-calendar-draft-border gradient (currentColor),
    // so we only need the text/icon color here.
    return colors.draftText
  }

  return `${colors.bg} ${props.image ? 'text-inverse' : colors.text}`
})

// Inline style for custom items with a dynamic color (Tailwind can't generate arbitrary CSS for runtime values)
const colorStyle = computed(() => {
  if (props.item.type === 'customItem' && props.item.color) {
    return isDraft.value
      // color drives both the text and the dashed-border gradient (currentColor)
      ? { color: props.item.color }
      : { backgroundColor: props.item.color, color: getContrastColor(props.item.color) }
  }
  return undefined
})

// Spread the event image across the full 7-column week grid using a fixed px reference
// (--calendar-cell-width set by AM3MarketingCalendar on the grid container).
// Absolute px values avoid the seam caused by each cell's margin-adjusted width being
// slightly different, which makes percentage-based background-size inconsistent.
// Single-day items (including all modal renderings) use 100% so the image fills
// the element without needing the grid's --calendar-cell-width CSS variable.
// Multi-day items use the grid-relative approach for seamless cross-cell continuity.
const imageBackgroundStyle = computed(() => {
  if (props.totalSpanDays <= 1) {
    return {
      backgroundImage: `url(${props.image})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
    }
  }
  return {
    backgroundImage: `url(${props.image})`,
    backgroundSize: `calc(${props.totalSpanDays} * var(--calendar-cell-width, 200px)) auto`,
    backgroundPositionX: `calc(${props.dayOffset} * -1 * var(--calendar-cell-width, 200px))`,
    backgroundPositionY: 'center',
    backgroundRepeat: 'no-repeat',
  }
})

// Get icon type based on item type
const iconType = computed(() => {
  switch (props.item.type) {
    case 'event':
      return 'calendar'
    case 'email':
      return 'email'
    case 'sms':
      return 'sms'
    case 'presale':
      return 'ticket'
    case 'signup':
      return 'user-plus'
    case 'waitlist':
      return 'clock'
    case 'competition':
      return 'trophy'
    case 'customItem':
      return 'calendar'
    default:
      return undefined
  }
})
</script>

<style scoped>
/*
 * Fine-grained dashed border for drafts. An SVG stroked rect (rather than
 * `border-dashed` or edge gradients) lets the dashes follow the rounded corners.
 * Geometry and dash pattern are set via CSS so they're editable in one place:
 *   --radius     corner radius (match the element's rounded-md = 6px)
 *   --thickness  stroke weight
 *   --dash/--gap dash length and gap
 * `currentColor` inherits the draft's text color, which equals the per-type
 * border color in every case. The 1px inset keeps the centered stroke fully
 * inside the box so the parent's overflow-hidden doesn't clip its outer edge.
 */
.am3-calendar-draft-rect {
  --radius: 4px;
  --thickness: 1px;
  --dash: 8px;
  --gap: 4px;
  x: 1px;
  y: 1px;
  width: calc(100% - 2px);
  height: calc(100% - 2px);
  rx: var(--radius);
  ry: var(--radius);
  fill: none;
  stroke: currentColor;
  stroke-width: var(--thickness);
  stroke-dasharray: var(--dash) var(--gap);
}
</style>
